import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [accountStore, setAccountStore] = useState(JSON.parse(localStorage.getItem('store') || 'null'));
  const [impersonatedStore, setImpersonatedStore] = useState(JSON.parse(localStorage.getItem('siteViewStore') || 'null'));
  const [viewMode, setViewModeState] = useState(localStorage.getItem('adminViewMode') || 'admin');
  const [loading, setLoading] = useState(true);

  const verifyWithTimeout = async (requestPromise, timeoutMs = 5000) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Auth verification timed out')), timeoutMs);
    });

    return Promise.race([requestPromise, timeoutPromise]);
  };

  useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const response = await verifyWithTimeout(api.get('/auth/me'));
          setAccountStore(response.data.store);
          localStorage.setItem('store', JSON.stringify(response.data.store));
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const login = (newToken, storeData) => {
    setToken(newToken);
    setAccountStore(storeData);
    setImpersonatedStore(null);
    localStorage.setItem('token', newToken);
    localStorage.setItem('store', JSON.stringify(storeData));
    localStorage.removeItem('siteViewStore');
    setViewModeState('admin');
    localStorage.setItem('adminViewMode', 'admin');
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    setToken(null);
    setAccountStore(null);
    setImpersonatedStore(null);
    localStorage.removeItem('token');
    localStorage.removeItem('store');
    localStorage.removeItem('siteViewStore');
    localStorage.removeItem('adminViewMode');
    delete api.defaults.headers.common['Authorization'];
  };

  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem('adminViewMode', mode);
  };

  const viewAsStore = (storeData) => {
    setImpersonatedStore(storeData);
    localStorage.setItem('siteViewStore', JSON.stringify(storeData));
    setViewMode('site');
  };

  const store = viewMode === 'site' && impersonatedStore ? impersonatedStore : accountStore;

  const value = {
    token,
    store,
    accountStore,
    viewMode,
    setViewMode,
    viewAsStore,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
